import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { SectionHeader } from "@/components/common/Widgets";
import { Sparkles, Download, Loader2 } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { pollJob } from "@/lib/jobs";

export default function SFormatSection({ app, sformat, reload }) {
  const [busy, setBusy] = useState(false);
  const [jobStatus, setJobStatus] = useState(null); // {status, attempts, error}
  const [progressPct, setProgressPct] = useState(0);
  // De-brand: dynamic header based on bank
  const bankName = (() => {
    try { return JSON.parse(localStorage.getItem("acas_bank") || "{}").short_name || "Bank"; } catch { return "Bank"; }
  })();

  const generate = async () => {
    setBusy(true);
    setProgressPct(8);
    setJobStatus({ status: "QUEUED" });
    try {
      // Kick off the async job
      const start = await api.post("/ai/generate-s-format/async", { application_id: app.id });
      const jobId = start.data.job_id;
      let tick = 8;
      const final = await pollJob(jobId, {
        intervalMs: 2500,
        onProgress: (j) => {
          setJobStatus({ status: j.status, attempts: j.attempts, error: j.error });
          // Soft fake progress to give the user feedback (LLM call is opaque)
          if (j.status === "RUNNING") {
            tick = Math.min(90, tick + 7);
            setProgressPct(tick);
          } else if (j.status === "RETRYING") {
            setProgressPct(30);
          }
        },
      });
      if (final.status === "SUCCESS") {
        setProgressPct(100);
        toast.success("S-Format appraisal generated");
        reload && reload();
      } else {
        throw new Error(final.error || "Job failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Generation failed");
    } finally {
      setBusy(false);
      setTimeout(() => { setJobStatus(null); setProgressPct(0); }, 1500);
    }
  };
  const download = async () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("acas_token");
    try {
      const r = await fetch(`${BACKEND_URL}/api/exports/s-format/${app.id}.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("download");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `SFormat_${app.ref_no}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };
  return (
    <div className="space-y-3">
      <SectionHeader title={`S-Format Appraisal Note (${bankName})`} subtitle="AI-generated structured appraisal note for sanction proposal." action={(
        <div className="flex gap-2">
          <Button variant="outline" disabled={!sformat} onClick={download} data-testid="sformat-download-button"><Download className="h-4 w-4 mr-1" />Download PDF</Button>
          <Button onClick={generate} disabled={busy} data-testid="sformat-generate-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}{busy ? "Generating…" : (sformat ? "Regenerate" : "Generate with AI")}</Button>
        </div>
      )} />
      {busy && (
        <Card className="p-4 border-accent/30 bg-accent/5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-accent" />
              <span>S-Format generation in progress…</span>
              {jobStatus?.status && <Badge variant="outline" className="text-[10px]" data-testid="sformat-job-status">{jobStatus.status}</Badge>}
              {jobStatus?.attempts > 1 && <Badge variant="secondary" className="text-[10px]">Attempt {jobStatus.attempts}</Badge>}
            </div>
            <span className="text-xs text-muted-foreground mono">{progressPct}%</span>
          </div>
          <Progress value={progressPct} className="h-1.5" />
          <p className="text-[11px] text-muted-foreground mt-2">This usually takes 20-60 seconds. You can navigate away — the job will continue in the background.</p>
        </Card>
      )}
      {sformat && (
        <Card className="p-5">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-4">
            <Badge variant="outline">Model: {sformat.model}</Badge>
            <span className="mono">Generated {fmtDate(sformat.generated_at)} by {sformat.generated_by}</span>
            <span className="mono">· {sformat.length_chars} chars</span>
          </div>
          <article className="prose prose-sm max-w-none dark:prose-invert">
            <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans bg-muted/30 border rounded-md p-4 max-h-[calc(100vh-300px)] overflow-auto" data-testid="sformat-content">{sformat.content_markdown}</pre>
          </article>
        </Card>
      )}
      {!sformat && !busy && (
        <Card className="p-10 text-center">
          <Sparkles className="h-10 w-10 text-accent mx-auto mb-3" />
          <div className="text-base font-semibold heading">No appraisal note yet</div>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Once CMA, CRA and assessments are in place, click “Generate with AI” to produce a complete S-Format appraisal note.</p>
        </Card>
      )}
    </div>
  );
}
